import { Box, Button, IconButton, Slide, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isDarkMode } from "../utils/constants";
import { checkUserHasSubscription } from "../utils/user";

const MINIMUM_QUESTIONS_BEFORE_BANNER = 3;

const countUserQuestions = (messages) => {
  if (!Array.isArray(messages)) return 0;

  return messages.reduce((count, message) => {
    const sender = String(message?.sender || "").toLowerCase();
    if (sender !== "human" && sender !== "user") return count;
    if (message?.isButton) return count;

    const text = String(message?.message || "").trim();
    const hasAttachments =
      Array.isArray(message?.attachments) && message.attachments.length > 0;

    return text.length > 0 || hasAttachments ? count + 1 : count;
  }, 0);
};

const ProBanner = ({
  userData,
  isLoggedIn,
  loadingUserData,
  isSidebarOpen = false,
  isDataProcessesOpen = false,
  messageThreads = [],
}) => {
  const [open, setOpen] = useState(false);
  const [proBannerText, setProBannerText] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const hasSubscription = checkUserHasSubscription(userData);
  const eligibleForProTrial = !!userData?.eligible_for_pro_trial;
  const proTrialDays = Number(userData?.pro_trial_days) > 0 ? Number(userData.pro_trial_days) : 7;
  const questionCount = countUserQuestions(messageThreads);
  const hasReachedQuestionGate =
    userData?.show_try_incite_pro_banner === true ||
    questionCount >= MINIMUM_QUESTIONS_BEFORE_BANNER;
  const canShowBanner =
    isLoggedIn &&
    !loadingUserData &&
    !hasSubscription &&
    !dismissed &&
    hasReachedQuestionGate;
  const navigate = useNavigate();

  const handleClose = () => {
    setOpen(false);
    setDismissed(true);
  };

  // Parse `{...}` as the CTA part
  const parseBannerText = (raw) => {
    if (!raw) return { prefix: "", cta: null, suffix: "" };
    const open = raw.indexOf("{");
    const close = open >= 0 ? raw.indexOf("}", open + 1) : -1;

    if (open === -1 || close === -1) {
      // no braces – everything is just plain text
      return { prefix: raw, cta: null, suffix: "" };
    }

    const prefix = raw.slice(0, open).trimEnd();
    const cta = raw.slice(open + 1, close).trim();
    const suffix = raw.slice(close + 1).trimStart();

    return { prefix, cta: cta || null, suffix };
  };

  const handleCtaClick = () => {
    navigate("/plan-selection");
  };

  // Fetch banner config only after the user has asked enough questions.
  useEffect(() => {
    if (!canShowBanner) {
      setOpen(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch("/graphql", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query: `{
              admin {
                proBanner {
                  text
                  enabled
                }
              }
            }`,
          }),
        });
        const json = await res.json();
        const banner = json?.data?.admin?.proBanner;
        if (cancelled) return;

        if (banner && banner.enabled && banner.text) {
          setProBannerText(banner.text);
        } else {
          setProBannerText(null);
        }
      } catch (e) {
        console.error("failed to fetch pro banner", e);
        if (!cancelled) setProBannerText(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canShowBanner]);

  // Animate open once text is ready
  useEffect(() => {
    if (!proBannerText || !canShowBanner) return;
    const t = setTimeout(() => setOpen(true), 200);
    return () => clearTimeout(t);
  }, [proBannerText, canShowBanner]);

  if (!proBannerText || !canShowBanner) return null;

  const { prefix, cta, suffix } = parseBannerText(proBannerText);
  const kickerText = eligibleForProTrial ? `${proTrialDays}-Day Free Trial` : "Incite Pro";
  const LAUNCHER_SAFE_INSET = 56;
  const leftInsetDesktop = isSidebarOpen ? 312 : LAUNCHER_SAFE_INSET;
  const rightInsetDesktop = isDataProcessesOpen ? 292 : LAUNCHER_SAFE_INSET;
  const leftInsetMobile = isSidebarOpen ? 8 : LAUNCHER_SAFE_INSET;
  const rightInsetMobile = LAUNCHER_SAFE_INSET;
  const palette = isDarkMode
    ? {
        card: "#131520",
        border: "rgba(129, 159, 246, 0.32)",
        kickerBackground: "#2A3C70",
        kickerText: "#EAF0FF",
        title: "#EAF0FF",
        ctaBackground: "#819FF6",
        ctaText: "#0E1626",
        closeBackground: "#2A3C70",
        closeIcon: "#EAF0FF",
      }
    : {
        card: "#EEF3FF",
        border: "rgba(43, 91, 184, 0.25)",
        kickerBackground: "rgba(43, 91, 184, 0.12)",
        kickerText: "#2B5BB8",
        title: "#032343",
        ctaBackground: "#2D61CF",
        ctaText: "#FFFFFF",
        closeBackground: "rgba(3, 35, 67, 0.08)",
        closeIcon: "#032343",
      };
  const headlineText = [prefix, suffix].filter(Boolean).join(" ").trim();

  return (
    <Box
      sx={{
        zIndex: 1901,
        position: "fixed",
        top: 6,
        left: { xs: `${leftInsetMobile}px`, md: `${leftInsetDesktop}px` },
        right: { xs: `${rightInsetMobile}px`, md: `${rightInsetDesktop}px` },
        boxSizing: "border-box",
        minWidth: 0,
        pointerEvents: "none",
      }}
    >
      <Slide in={open} direction="down" mountOnEnter unmountOnExit>
        <Box
          sx={{
            pt: "4px",
            pb: "2px",
            width: "100%",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: "980px",
              mx: "auto",
              minHeight: 74,
              boxSizing: "border-box",
              borderRadius: "14px",
              border: `1px solid ${palette.border}`,
              backgroundColor: palette.card,
              py: "10px",
              pl: "12px",
              pr: "44px",
              pointerEvents: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  borderRadius: "999px",
                  px: "8px",
                  py: "3px",
                  mb: "6px",
                  backgroundColor: palette.kickerBackground,
                }}
              >
                <Typography
                  component="p"
                  sx={{
                    fontSize: "10px",
                    fontWeight: 700,
                    letterSpacing: "0.4px",
                    lineHeight: "12px",
                    textTransform: "uppercase",
                    color: palette.kickerText,
                  }}
                >
                  {kickerText}
                </Typography>
              </Box>

              {headlineText && (
                <Typography
                  component="p"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "18px",
                    color: palette.title,
                    minWidth: 0,
                  }}
                >
                  {headlineText}
                </Typography>
              )}

              {cta && (
                <Button
                  onClick={handleCtaClick}
                  endIcon={<ChevronRightRoundedIcon sx={{ fontSize: 18 }} />}
                  disableElevation
                  sx={{
                    mt: "8px",
                    minHeight: "32px",
                    borderRadius: "10px",
                    px: "10px",
                    py: "7px",
                    textTransform: "none",
                    fontSize: "12px",
                    fontWeight: 700,
                    lineHeight: "16px",
                    backgroundColor: palette.ctaBackground,
                    color: palette.ctaText,
                    "&:hover": {
                      backgroundColor: palette.ctaBackground,
                      filter: "brightness(0.95)",
                    },
                    "& .MuiButton-endIcon": {
                      ml: "2px",
                    },
                  }}
                >
                  {cta}
                </Button>
              )}
            </Box>

            <IconButton
              size="small"
              aria-label="Dismiss Incite Pro banner"
              onClick={handleClose}
              sx={{
                position: "absolute",
                right: "8px",
                top: "8px",
                width: "24px",
                height: "24px",
                borderRadius: "12px",
                backgroundColor: palette.closeBackground,
                color: palette.closeIcon,
                "&:hover": {
                  backgroundColor: palette.closeBackground,
                  filter: "brightness(0.95)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Slide>
    </Box>
  );
};

export default ProBanner;
